<template>
  <Menu as="div" class="relative self-center min-w-0" data-testid="organization-switcher">
    <MenuButton
      class="flex max-w-[11rem] sm:max-w-xs items-center gap-x-2 rounded-md px-2 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-white dark:hover:bg-white/5 cursor-pointer"
    >
      <span class="sr-only">Switch organization. Current:</span>
      <BuildingOffice2Icon class="size-5 shrink-0 text-gray-400" aria-hidden="true" />
      <span class="truncate" data-testid="organization-current">{{
        current?.name ?? (organizations.error ? 'Organizations unavailable' : 'Loading…')
      }}</span>
      <span
        v-if="current && current.role !== 'OWNER'"
        class="hidden sm:inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-medium inset-ring"
        :class="ROLE_INFO[current.role].badge"
        data-testid="organization-role"
        >{{ roleLabel(current.role) }}</span
      >
      <ChevronUpDownIcon class="size-4 shrink-0 text-gray-400" aria-hidden="true" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute left-0 z-10 mt-2 w-72 max-w-[calc(100vw-2rem)] origin-top-left rounded-md bg-white py-1 shadow-lg outline-1 outline-gray-900/5 dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10"
      >
        <p class="px-3 pt-2 pb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Organizations
        </p>
        <MenuItem
          v-for="organization in organizations.organizations"
          :key="organization.id"
          v-slot="{ active }"
        >
          <button
            type="button"
            :class="[
              active ? 'bg-gray-50 dark:bg-white/5' : '',
              'flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm text-gray-900 dark:text-white cursor-pointer',
            ]"
            :aria-current="organization.id === organizations.selectedId ? 'true' : undefined"
            :data-organization-id="organization.id"
            @click="switchTo(organization.id)"
          >
            <CheckIcon
              class="size-4 shrink-0"
              :class="
                organization.id === organizations.selectedId
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'invisible'
              "
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1 truncate">{{ organization.name }}</span>
            <span class="shrink-0 text-xs text-gray-500 dark:text-gray-400">{{
              roleLabel(organization.role)
            }}</span>
          </button>
        </MenuItem>
        <MenuItem v-if="organizations.error && !organizations.loaded" v-slot="{ active }">
          <button
            type="button"
            :class="[
              active ? 'bg-gray-50 dark:bg-white/5' : '',
              'block w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 cursor-pointer',
            ]"
            @click="retry"
          >
            {{ organizations.error }} Try again
          </button>
        </MenuItem>
        <div class="my-1 border-t border-gray-100 dark:border-white/10" />
        <MenuItem v-slot="{ active }">
          <RouterLink
            :to="{ name: 'OrganizationSettings' }"
            :class="[
              active ? 'bg-gray-50 dark:bg-white/5' : '',
              'flex items-center gap-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300',
            ]"
          >
            <Cog6ToothIcon class="size-4 text-gray-400" aria-hidden="true" />
            Organization settings
          </RouterLink>
        </MenuItem>
        <MenuItem v-slot="{ active }">
          <button
            type="button"
            :class="[
              active ? 'bg-gray-50 dark:bg-white/5' : '',
              'flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 cursor-pointer',
            ]"
            @click="createOpen = true"
          >
            <PlusIcon class="size-4 text-gray-400" aria-hidden="true" />
            Create organization
          </button>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>

  <CreateOrganizationDialog v-model="createOpen" @created="onCreated" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { BuildingOffice2Icon, Cog6ToothIcon, PlusIcon } from '@heroicons/vue/24/outline';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import CreateOrganizationDialog from '@/components/CreateOrganizationDialog.vue';
import { useOrganizationStore } from '@/stores/organization';
import { useToastStore } from '@/stores/toast';
import { ROLE_INFO, roleLabel } from '@/lib/organizations';
import type { Organization } from '@/api/organizations';
import { refreshOrganizations } from '@/lib/api';

const organizations = useOrganizationStore();
const toast = useToastStore();
const route = useRoute();
const router = useRouter();
const current = computed(() => organizations.current);
const createOpen = ref(false);

// Runner pages address a runner of the previous organization.
const ORGANIZATION_INDEPENDENT_ROUTES = ['OrganizationSettings', 'Profile', 'Billing'];

async function switchTo(id: string) {
  if (id === organizations.selectedId) return;
  organizations.select(id);
  // Forced so the guards re-check role and plan of the new organization even on the same route.
  const target = ORGANIZATION_INDEPENDENT_ROUTES.includes(route.name as string)
    ? { path: route.path, query: route.query }
    : { name: 'Dashboard' };
  await router.replace({ ...target, force: true });
}

async function retry() {
  await refreshOrganizations();
}

async function onCreated(organization: Organization) {
  toast.success(`Organization “${organization.name}” created.`);
  await router.replace({ name: 'OrganizationSettings', force: true });
}
</script>
